import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import { Row, Col } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { MecordColors } from "Constants/Color";
import { format, parse } from "date-fns";
import {
  initialServiceQuotationValues,
  ServiceQuotationFormValues,
  DEFAULT_ORGANISATION,
} from "./serviceInitialValues";
import BillingFromAddress from "Common/DynamicComponentsForEnquiryQuotationSalesOrder/BillingFromAddress";
import QuotationDetailsSection from "./Sections/QuotationDetailsSection";
import CustomerDetailsSection from "./Sections/CustomerDetailsSection";
import AgentDetailsSection from "./Sections/AgentDetailsSection";
import AssignMembersSection from "./Sections/AssignMembersSection";
import AssignEngineersSection from "./Sections/AssignEngineersSection";
import ServiceListSection from "./Sections/ServiceListSection";
import TermsConditionsSection from "./Sections/TermsConditionsSection";
import AddTagsForQuotation from "./Sections/AddTagsForQuotation";
import NotesAttachmentsSection from "./Sections/NotesAttachmentsSection";
import FollowUpSection from "./Sections/FollowUpSection";
import TotalSummarySection from "./Sections/TotalSummarySection";
import FormActionButtons from "./Components/FormActionButtons";
import { mapApiAccessoryToItem } from "./Components/ServiceAccessoryRow";
import { ArrowBack } from "@mui/icons-material";
import {
  createServiceQuotation,
  fetchServiceQuotationById,
  updateServiceQuotation,
} from "slices/quotation";
import { fetchServiceEnquiryById } from "slices/enquiry/thunk";
import { mapEnquiryToQuotationForm } from "./utils/mapEnquiryToQuotationForm";
import * as Yup from "yup";
import { clearAllTableStates } from "utils/clearTableStates";
import LoadingSpinner from "components/LoadingSpinner";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";
import type { FormikErrors } from "formik";

function ScrollToFormikErrorWrapper({
  errors,
  submitCount,
  children,
}: {
  errors: FormikErrors<any>;
  submitCount: number;
  children: React.ReactNode;
}) {
  useScrollToFormikError(errors, submitCount);
  return <>{children}</>;
}

// Transform backend service quotation data to form format
const transformServiceQuotationDataToForm = (
  quotationData: any
): ServiceQuotationFormValues => {
  const {
    quotation_service_id,
    quotation_number,
    quotation_date,
    valid_until_date,
    initiated_by,
    enquiry_service_id,
    enquiry_number,
    uid_number,
    quotation_stage,
    customer_details,
    customer_name,
    agent_name,
    quotation_status,
    notes,
    attachments,
    terms_and_conditions,
    quotation_service_items,
    assigned_members,
    follow_up_date,
  } = quotationData;

  // Transform customer details
  const company = customer_details
    ? {
      company_id: customer_details.company_id || customer_details.customer_id,
      company_name:
        customer_details.company_name || customer_details.customer_name,
      company_email: customer_details.company_email || customer_details.email,
      phone_number: customer_details.phone_number,
      gstin: customer_details.gstin,
      gst_type: customer_details.gst_type || customer_details.type,
      vendor_code: customer_details.vendor_code || "",
      branch_name: customer_details.branch_name || "",
      industry: customer_details.industry,
      billing_address: customer_details.billing_address,
      shipping_addresses:
        customer_details.shipping_addresses ||
        customer_details.shipping_address ||
        [],
      contacts:
        customer_details.contact_details || customer_details.contacts || [], // Include contacts for the component to work properly
    }
    : null;

  // Transform contact persons
  const contact_persons =
    (customer_details?.contact_details || customer_details?.contacts)?.map(
      (contact: any) => ({
        id: contact.contact_id || contact.id,
        name: contact.name || contact.contact_name,
        designation: contact.designation || contact.contact_person_designation,
        phone_country:
          contact.phone_country || contact.contact_person_phone_country,
        phone_number: contact.phone_number || contact.contact_person_phone_no,
        email: contact.email || contact.contact_person_email,
      })
    ) || [];

  // Transform assigned members
  const members =
    assigned_members?.map((member: any) => {
      const firstName = member.first_name || "";
      const lastName = member.last_name || "";
      const initials = (
        (firstName.charAt(0) || "") + (lastName.charAt(0) || "")
      ).toUpperCase();
      return {
        id: member.user_id,
        name:
          member.username ||
          `${firstName} ${lastName}`.trim() ||
          member.first_name,
        first_name: firstName,
        last_name: lastName,
        email: member.email,
        initials:
          initials ||
          (member.username
            ? member.username.substring(0, 2).toUpperCase()
            : ""),
      };
    }) || [];

  // Transform services
  const services =
    quotation_service_items?.map((item: any, index: number) => {
      // Extract and normalize shipping address object
      let serviceAddressObject = null;

      if (item.shipping_address) {
        // Handle array format (backend returns array)
        if (
          Array.isArray(item.shipping_address) &&
          item.shipping_address.length > 0
        ) {
          const firstAddress = item.shipping_address[0];
          // Normalize the address object - ensure consistent field names
          serviceAddressObject = {
            shipping_address_id:
              firstAddress.shipping_address_id || firstAddress.id,
            company_id: firstAddress.company_id,
            address: firstAddress.address,
            city: firstAddress.city,
            state: firstAddress.state,
            country: firstAddress.country,
            pincode: firstAddress.pincode || firstAddress.pin_code,
            phone_number: firstAddress.phone_number,
            phone_country: firstAddress.phone_country,
            is_default: firstAddress.is_default || false,
            created_at: firstAddress.created_at,
            updated_at: firstAddress.updated_at,
          };
        }
        // Handle object format
        else if (typeof item.shipping_address === "object") {
          // Normalize the address object - ensure consistent field names
          serviceAddressObject = {
            shipping_address_id:
              item.shipping_address.shipping_address_id ||
              item.shipping_address.id,
            company_id: item.shipping_address.company_id,
            address: item.shipping_address.address,
            city: item.shipping_address.city,
            state: item.shipping_address.state,
            country: item.shipping_address.country,
            pincode:
              item.shipping_address.pincode || item.shipping_address.pin_code,
            phone_number: item.shipping_address.phone_number,
            phone_country: item.shipping_address.phone_country,
            is_default: item.shipping_address.is_default || false,
            created_at: item.shipping_address.created_at,
            updated_at: item.shipping_address.updated_at,
          };
        }
        // Handle string format (legacy - should not happen, but handle for backward compatibility)
        else if (typeof item.shipping_address === "string") {
          // Keep as null - will need to be selected from dropdown
          serviceAddressObject = null;
        }
      }

      return {
        // Keep stable quotation line id (not product_id) so updates don't recreate
        // lines and break order↔quotation challan lock lineage.
        id: item.quotation_service_item_id || item.id,
        product_id: item.product_id,
        sr_no: index + 1,
        // Model fields
        model: item.model_name || "",
        model_name: item.model_name || "",
        // Product/Service fields - use camelCase for form compatibility
        product: item.product_name || "",
        serviceName: item.product_name || "", // camelCase for form
        item: item.product_id || "",
        item_name: item.product_name || "",
        // Serial number
        serial_no: item.serial_number || "",
        serialNo: item.serial_number || "", // camelCase for form
        // Service category, method, type, execution mode - camelCase
        service_category: item.service_category || "",
        serviceCategory: item.service_category || "", // camelCase for form
        service_method: item.service_method || "",
        serviceMethod: item.service_method || "", // camelCase for form
        service_type: item.service_type || "",
        serviceType: item.service_type || "", // camelCase for form
        execution_mode: item.execution_mode || "",
        executionMode: item.execution_mode || "", // camelCase for form
        // Quantity
        product_quantity: item.product_quantity ?? 1,
        productQuantity:
          item.product_quantity != null
            ? item.product_quantity.toString()
            : "1", // camelCase for form
        quantity: item.product_quantity ?? 1,
        // Service rate/amount
        service_amount: item.unit_price?.toString() || "0",
        serviceRate: item.unit_price?.toString() || "0", // camelCase for form
        unit_price: item.unit_price || 0,
        // Address - always store as full object (null if not available)
        service_address: serviceAddressObject,
        serviceAddress: serviceAddressObject, // camelCase for form - always full object
        // Reported complaint
        reported_complaint: item.reported_complaints || "",
        reportedComplaint: item.reported_complaints || "", // camelCase for form
        // Category fields
        category: item.category_name || "",
        category_name: item.category_name || "",
        sub_category: item.sub_category_name || "",
        sub_category_name: item.sub_category_name || "",
        // Make fields
        make: item.make_name || "",
        make_name: item.make_name || "",
        // Description and additional details
        prod_description: item.prod_description ?? "",
        additional_details: item.additional_details ?? "",
        // Unit fields
        unit: item.unit_name || "",
        unit_name: item.unit_name || "",
        // HSN fields
        hsn: item.hsn_code || "",
        hsn_code: item.hsn_code || "",
        // SAC fields
        sac: item.sac_code || "",
        sac_code: item.sac_code || "",
        // SKU fields
        sku: item.product_sku || "",
        product_sku: item.product_sku || "",
        // GST fields
        gst_percent: item.gst_percentage || 0,
        gst_percentage: item.gst_percentage || 0,
        cgst_percentage: item.cgst_percentage !== undefined && item.cgst_percentage !== null ? Number(item.cgst_percentage) : undefined,
        sgst_percentage: item.sgst_percentage !== undefined && item.sgst_percentage !== null ? Number(item.sgst_percentage) : undefined,
        igst_percentage: item.igst_percentage !== undefined && item.igst_percentage !== null ? Number(item.igst_percentage) : undefined,
        gst_value: item.gst_value || 0,
        // Value fields - ensure all calculated fields are present
        gross_value: item.gross_value || (item.unit_price && item.product_quantity
          ? item.unit_price * item.product_quantity
          : 0),
        // Discount fields - calculate calculated_discount_value from raw discount_value
        discount_type: item.discount_type || "currency",
        discount_value: item.discount_value || 0, // raw value from backend
        calculated_discount_value: (() => {
          // Helper function to round to 2 decimal places
          const roundToTwoDecimals = (num: number): number => Math.round(num * 100) / 100;

          const grossValue = item.gross_value || (item.unit_price && item.product_quantity
            ? item.unit_price * item.product_quantity
            : 0);
          const discountValueInput = parseFloat(item.discount_value) || 0;
          const discountType = item.discount_type || "currency";

          let calculatedDiscount = 0;
          if (discountType === "percentage") {
            calculatedDiscount = roundToTwoDecimals((grossValue * discountValueInput) / 100);
          } else {
            // Currency discount
            calculatedDiscount = roundToTwoDecimals(Math.min(discountValueInput, grossValue));
          }

          return calculatedDiscount;
        })(),
        net_value: item.net_value || item.total_service_net_amount || 0,
        total: item.total || item.total_price || item.total_service_amount || 0,
        total_price: item.total_price || item.total_service_amount || 0,
        total_service_amount: item.total_service_amount || 0,
        // Other fields
        remark: item.remark || "",
        product_type: item.product_type || [],
        type: "service",
        // Backend compatibility fields
        quotation_service_item_id: item.quotation_service_item_id,
        has_challan: Boolean(item.has_challan || item.is_locked),
        is_locked: Boolean(item.has_challan || item.is_locked),
        accessories: Array.isArray(item.accessories)
          ? item.accessories.map((acc: any) => mapApiAccessoryToItem(acc))
          : [],
      };
    }) || [];

  // Transform terms and conditions - group by type to handle multiple options per type
  let transformedTermsAndConditions: any[] = [];
  if (Array.isArray(terms_and_conditions) && terms_and_conditions.length > 0) {
    // Group terms by type
    const groupedByType: Record<string, string[]> = {};
    terms_and_conditions.forEach((term: any) => {
      if (term.type && term.options) {
        if (!groupedByType[term.type]) {
          groupedByType[term.type] = [];
        }
        // Add option if not already present
        if (!groupedByType[term.type].includes(term.options)) {
          groupedByType[term.type].push(term.options);
        }
      }
    });

    // Convert grouped data to form format
    transformedTermsAndConditions = Object.entries(groupedByType).map(
      ([type, options], index) => ({
        id: `term-${index}-${Date.now()}-${Math.random()}`,
        type: type,
        option: options[0] || "", // First option for backward compatibility
        description: options.join("\n"), // All options as newline-separated
      })
    );
  } else if (terms_and_conditions && typeof terms_and_conditions === "object") {
    // Handle case where it's an object instead of array
    const termsArray = Object.values(terms_and_conditions);
    const groupedByType: Record<string, string[]> = {};
    termsArray.forEach((term: any) => {
      if (term.type && term.options) {
        if (!groupedByType[term.type]) {
          groupedByType[term.type] = [];
        }
        if (!groupedByType[term.type].includes(term.options)) {
          groupedByType[term.type].push(term.options);
        }
      }
    });

    transformedTermsAndConditions = Object.entries(groupedByType).map(
      ([type, options], index) => ({
        id: `term-${index}-${Date.now()}-${Math.random()}`,
        type: type,
        option: options[0] || "",
        description: options.join("\n"),
      })
    );
  }

  // Helper function to convert UTC date to local date string (YYYY-MM-DD format)
  // Backend returns UTC dates, we need to convert to local for display
  const formatDateToDisplay = (utcDateStr: string): string => {
    if (!utcDateStr) return "";
    try {
      // Parse UTC date string
      const utcDate = new Date(utcDateStr);
      if (isNaN(utcDate.getTime())) return "";

      // Get local date components
      const year = utcDate.getFullYear();
      const month = String(utcDate.getMonth() + 1).padStart(2, "0");
      const day = String(utcDate.getDate()).padStart(2, "0");

      // Return in YYYY-MM-DD format (for HTML date input)
      return `${year}-${month}-${day}`;
    } catch {
      return "";
    }
  };

  return {
    quotation_date: quotation_date ? formatDateToDisplay(quotation_date) : "",
    valid_until: valid_until_date ? formatDateToDisplay(valid_until_date) : "",
    enquiry_reference_number: enquiry_number || "",
    enquiry_service_id: enquiry_service_id || "",
    uid_number: uid_number || "",
    reference_number: quotationData.reference_number || "",
    initiated_by: initiated_by || "",
    company,
    contact_persons,
    agent_name: agent_name || "",
    members,
    assigned_technician: (quotationData.assigned_technician || []).map((t: any) => ({
      ...t,
      id: t.user_id || t.id,
      user_id: t.user_id || t.id,
      first_name: t.first_name || "",
      last_name: t.last_name || "",
      username: t.username || "",
    })),
    services,
    terms_and_conditions: transformedTermsAndConditions,
    notes: notes || "",
    attachments: attachments || [],
    next_follow_up_date: follow_up_date
      ? formatDateToDisplay(follow_up_date)
      : "",
    quotation_id: quotation_service_id,
    has_order: Boolean(quotationData.has_order || quotationData.sales_order_service_id),
    sales_order_service_id: quotationData.sales_order_service_id || null,
    quotation_status: quotation_status || undefined,
    quotation_stage: quotation_stage || undefined,
    customer_details,
    tags: quotationData.tags || [],
    bill_from_address: (() => {
      const billFrom = quotationData.bill_from;
      if (
        billFrom &&
        typeof billFrom === "object" &&
        !Array.isArray(billFrom) &&
        Object.keys(billFrom).length > 0
      ) {
        return billFrom;
      }
      return DEFAULT_ORGANISATION;
    })(),
    follow_up_remarks: quotationData.follow_up_remarks || "",
    subtotal_before_discount: Number(quotationData.subtotal_before_discount || quotationData.sub_total_amount || 0),
    total_item_discounts: Number(quotationData.total_item_discounts || quotationData.total_discount_amount || 0),
    net_total: Number(quotationData.net_total || quotationData.net_amount || 0),
    total_gst_value: Number(quotationData.total_gst_value || quotationData.total_gst_amount || quotationData.gst_amount || 0),
  } as any as ServiceQuotationFormValues;
};

// Function to create validation schema based on edit mode
const createValidationSchema = (isEditMode: boolean) => {
  const baseSchema = {
    quotation_date: Yup.string().required("Quotation date is required"),
    valid_until: Yup.string().required("Valid until date is required"),
    enquiry_reference_number: Yup.string(),
    initiated_by: Yup.string().trim().optional(),
    company: Yup.object().nullable().required("Company selection is required"),
    contact_persons: Yup.array().min(
      1,
      "At least one contact person is required"
    ),
    members: Yup.array(),
    assigned_technician: Yup.array().notRequired(),
    services: Yup.array().min(1, "At least one service is required"),
    terms_and_conditions: Yup.array(),
  };

  // Only add follow-up validation in create mode
  if (!isEditMode) {
    (baseSchema as any).next_follow_up_date = Yup.string()
      .required("Next follow up date is required")
      .test(
        "not-before-quotation-date",
        "Next follow up date cannot be before quotation date",
        function (value: string) {
          const { quotation_date } = this.parent;
          if (!value || !quotation_date) return true;

          try {
            // next_follow_up_date is in YYYY-MM-DD format (from date input)
            // quotation_date is in yyyy-MMM-dd format (e.g., "2026-Jan-09")
            const followUpDate = new Date(value);
            const quotationDate = parse(
              quotation_date,
              "yyyy-MMM-dd",
              new Date()
            );

            if (
              isNaN(followUpDate.getTime()) ||
              isNaN(quotationDate.getTime())
            ) {
              return true; // Let other validations handle invalid dates
            }

            // Compare dates (ignore time)
            followUpDate.setHours(0, 0, 0, 0);
            quotationDate.setHours(0, 0, 0, 0);

            return followUpDate >= quotationDate;
          } catch {
            return true; // Let other validations handle parsing errors
          }
        }
      );
  }

  return Yup.object().shape(baseSchema);
};

const ServiceQuotationForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id, enquiryId } = useParams<{ id?: string; enquiryId?: string }>();
  const isEditMode = Boolean(id);
  const isCreateFromEnquiry = Boolean(enquiryId);

  const [initialValues, setInitialValues] =
    useState<ServiceQuotationFormValues>(initialServiceQuotationValues);
  const [isLoading, setIsLoading] = useState(isEditMode || isCreateFromEnquiry);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  useEffect(() => {
    if (isEditMode && id) {
      const fetchQuotationData = async () => {
        try {
          setIsLoading(true);
          const result = await dispatch(fetchServiceQuotationById(id) as any);

          if (fetchServiceQuotationById.fulfilled.match(result)) {
            const quotationData = result.payload;
            // Transform backend data to form format
            const transformedData =
              transformServiceQuotationDataToForm(quotationData);
            setInitialValues(transformedData);
          } else {
            toast.error("Failed to load quotation data");
            navigate("/quotation/service");
          }
        } catch (error) {
          console.error("Error fetching quotation:", error);
          toast.error("An error occurred while loading the quotation");
          navigate("/quotation/service");
        } finally {
          setIsLoading(false);
        }
      };

      fetchQuotationData();
    }
  }, [id, isEditMode, dispatch, navigate]);

  useEffect(() => {
    if (!isCreateFromEnquiry || !enquiryId || isEditMode) return;

    const loadEnquiryForQuotation = async () => {
      try {
        setIsLoading(true);
        const result = await dispatch(fetchServiceEnquiryById(enquiryId) as any);
        if (fetchServiceEnquiryById.fulfilled.match(result)) {
          const enquiryData = result.payload?.data || result.payload;
          const mapped = mapEnquiryToQuotationForm(enquiryData, "service");
          setInitialValues((prev) => ({
            ...prev,
            ...mapped,
          }));
        } else {
          toast.error("Failed to load enquiry data");
          navigate("/quotation/service/add");
        }
      } catch (error) {
        console.error("Error loading enquiry for quotation:", error);
        toast.error("An error occurred while loading the enquiry");
        navigate("/quotation/service/add");
      } finally {
        setIsLoading(false);
      }
    };

    loadEnquiryForQuotation();
  }, [enquiryId, isCreateFromEnquiry, isEditMode, dispatch, navigate]);

  const handleCancel = () => {
    navigate(-1);
  };

  const handleSaveDraft = async (values: ServiceQuotationFormValues) => {
    setIsSavingDraft(true);
    try {
      console.log("Save Draft:", values);

      if (isEditMode && id) {
        // Update existing quotation - preserve existing quotation_status
        const result = await dispatch(
          updateServiceQuotation({
            id,
            ...values,
            quotationStatus: values.quotation_status || undefined,
            quotationStage: "Draft",
          }) as any
        );

        if (updateServiceQuotation.fulfilled.match(result)) {
          toast.success("Service quotation updated as draft successfully!");
          navigate("/quotation/service");
        } else {
          console.error("Failed to update draft:", result.payload);
          toast.error(
            result.payload?.message ||
            "Failed to update draft. Please try again."
          );
        }
      } else {
        // Create new quotation
        const result = await dispatch(
          createServiceQuotation({
            formData: values,
            quotationStatus: "Prepared",
            quotationStage: "Draft",
          }) as any
        );

        if (createServiceQuotation.fulfilled.match(result)) {
          toast.success("Service quotation saved as draft successfully!");
          clearAllTableStates();
          navigate("/quotation/service");
        } else {
          console.error("Failed to save draft:", result.payload);
          toast.error(
            result.payload?.message || "Failed to save draft. Please try again."
          );
        }
      }
    } catch (error: any) {
      console.error("Error saving draft:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSave = async (values: ServiceQuotationFormValues) => {
    setIsSaving(true);
    try {
      console.log("Save:", values);

      if (isEditMode && id) {
        // Update existing quotation - preserve existing quotation_status
        const result = await dispatch(
          updateServiceQuotation({
            id,
            ...values,
            quotationStatus: values.quotation_status || undefined,
            quotationStage: "Log",
          }) as any
        );

        if (updateServiceQuotation.fulfilled.match(result)) {
          toast.success("Service quotation updated successfully!");
          navigate("/quotation/service");
        } else {
          console.error("Failed to update quotation:", result.payload);
          toast.error(
            result.payload?.message ||
            "Failed to update quotation. Please try again."
          );
        }
      } else {
        // Create new quotation
        const result = await dispatch(
          createServiceQuotation({
            formData: values,
            quotationStatus: "Prepared",
            quotationStage: "Log",
          }) as any
        );

        if (createServiceQuotation.fulfilled.match(result)) {
          toast.success("Service quotation created successfully!");
          clearAllTableStates();
          navigate("/quotation/service");
        } else {
          console.error("Failed to create quotation:", result.payload);
          toast.error(
            result.payload?.message ||
            "Failed to create quotation. Please try again."
          );
        }
      }
    } catch (error: any) {
      console.error("Error creating quotation:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        {/* Left: Back arrow + Title */}
        <div className="d-flex align-items-center">
          <ArrowBack
            className="me-2"
            style={{ fontSize: "1.25rem", cursor: "pointer" }}
            onClick={() => navigate(-1)}
          />
          <h5
            className="mb-0 fw-bold"
            style={{ fontFamily: "Poppins, sans-serif", fontSize: "17px" }}
          >
            <Link to={""} className="text-decoration-none text-dark">
              {isEditMode
                ? "Edit Service Quotation"
                : "Create Service Quotation"}
            </Link>
          </h5>
        </div>
        <LoadingSpinner label="Loading quotation..." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Formik
        initialValues={initialValues}
        validationSchema={createValidationSchema(isEditMode)}
        onSubmit={handleSave}
        enableReinitialize={true}
      >
        {({
          values,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          validateForm,
          setTouched,
          errors,
          submitCount,
          touched,
        }) => (
          (() => {
            const isBasicDetailsLocked = Boolean(
              (values as any).has_order || (values as any).sales_order_service_id
            );
            return (
          <ScrollToFormikErrorWrapper errors={errors} submitCount={submitCount}>
          <>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
              {/* Left: Back arrow + Title */}
              <div className="d-flex align-items-center">
                <ArrowBack
                  className="me-2"
                  style={{ fontSize: "1.25rem", cursor: "pointer" }}
                  onClick={() => navigate(-1)}
                />
                <h5
                  className="mb-0 fw-bold"
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "17px",
                  }}
                >
                  <Link to={""} className="text-decoration-none text-dark">
                    {isEditMode
                      ? "Edit Service Quotation"
                      : "Create Service Quotation"}
                  </Link>
                </h5>
              </div>

              {/* Top Action Buttons */}
              <FormActionButtons
                onCancel={handleCancel}
                showSaveDraft={!isEditMode}
                onSaveDraft={async () => {

                  setTouched({ company: true } as any);


                  if (!values.company) {

                    toast.error("Please select a company");

                    return;

                  }


                  handleSaveDraft(values);

                }}
                onSave={async () => {
                  // Mark all fields as touched to show validation errors
                  const fields = Object.keys(values);
                  const touchedFields: any = {};
                  fields.forEach((field) => {
                    touchedFields[field] = true;
                  });
                  setTouched(touchedFields);

                  // Validate form
                  const errors = await validateForm();

                  // If there are validation errors, don't save
                  // if (Object.keys(errors).length > 0) {
                  //   toast.error(
                  //     "Please fix validation errors before saving the quotation"
                  //   );
                  //   return;
                  // }

                  // If validation passes, submit the form
                  handleSubmit();
                }}
                isSaving={isSaving}
                isSavingDraft={isSavingDraft}
              />
            </div>

            <hr />

            <Form>
              {/* Quotation Details Section */}
              <Row className="mb-3">
                <Col xs={12} md={6} >
                  <QuotationDetailsSection type="service" />
                </Col>

                {/* Customer Details Section */}
                <Col xs={12} md={6}>
                  <CustomerDetailsSection />
                </Col>
              </Row>

              <hr />

              {/* Billing & Service Details Section */}
              <Row className="mb-3">
                <Col xs={12} md={6}>
                  <h5 className="fw-semibold mb-3">Billing & Service Details</h5>
                  <div
                    className="p-3 rounded mb-3"
                    style={{ backgroundColor: MecordColors?.LightGrayColor }}
                  >
                    <BillingFromAddress
                      value={values.bill_from_address}
                      disabled={isBasicDetailsLocked}
                      onChange={async (newValue) => {
                        await setFieldValue("bill_from_address", newValue);
                        setFieldTouched("bill_from_address", true);
                      }}
                      onBlur={async () => {
                        setFieldTouched("bill_from_address", true);
                      }}
                      error={touched.bill_from_address ? (errors.bill_from_address as string) : undefined}
                    />
                  </div>
                </Col>
              </Row>

              <hr />

              {/* Agent Details & Assign Members Section */}
              <Row className="mb-3">
                <Col xs={12} md={6}>
                  <AgentDetailsSection />
                </Col>
                <Col xs={12} md={6}>
                  <AssignMembersSection />
                </Col>
                <Col xs={12} md={6}>
                  <AssignEngineersSection />
                </Col>
              </Row>
              <Row className="mb-5">
                <Col xs={12} md={6}>
                  <AddTagsForQuotation
                    value={values.tags || []}
                    onChange={(tags) => setFieldValue("tags", tags)}
                  />
                </Col>
              </Row>

              <hr />

              {/* Service List Section - id for useScrollToFormikError when "at least one service" validation fails */}
              <Row className="mb-3" id="services">
                <Col xs={12}>
                  <ServiceListSection />
                </Col>
              </Row>

              <hr />

              <Row className="mb-3">
                {/* Total Summary Section */}
                <Col xs={12} md={6}>
                  <TermsConditionsSection />
                </Col>

                {/* Terms & Conditions Section */}
                <Col xs={12} sm={6}>
                  <TotalSummarySection />
                </Col>

                <hr />

                {/* Notes & Attachments Section */}
                <Col xs={12} md={6}>
                  <NotesAttachmentsSection />
                </Col>

                {/* Follow-up Section - only show in create mode */}
                {!isEditMode && (
                  <Col xs={12} md={6}>
                    <FollowUpSection />
                  </Col>
                )}
              </Row>

              <hr />

              {/* Bottom Action Buttons */}
              <Row className="mt-4">
                <Col xs={12}>
                  <FormActionButtons
                    onCancel={handleCancel}
                    showSaveDraft={!isEditMode}
                    onSaveDraft={async () => {

                      setTouched({ company: true } as any);


                      if (!values.company) {

                        toast.error("Please select a company");

                        return;

                      }


                      handleSaveDraft(values);

                    }}
                    onSave={async () => {
                      // Mark all fields as touched to show validation errors
                      const fields = Object.keys(values);
                      const touchedFields: any = {};
                      fields.forEach((field) => {
                        touchedFields[field] = true;
                      });
                      setTouched(touchedFields);

                      // Validate form
                      const errors = await validateForm();

                      // If there are validation errors, don't save
                      // if (Object.keys(errors).length > 0) {
                      //   toast.error(
                      //     "Please fix validation errors before saving the quotation"
                      //   );
                      //   return;
                      // }

                      // If validation passes, submit the form
                      handleSubmit();
                    }}
                    wrapperClassName="d-flex flex-wrap justify-content-center p-2 gap-md-3"
                    isSaving={isSaving}
                    isSavingDraft={isSavingDraft}
                  />
                </Col>
              </Row>
            </Form>
          </>
          </ScrollToFormikErrorWrapper>
            );
          })()
        )}
      </Formik>
    </PageContainer>
  );
};

export default ServiceQuotationForm;
